// src/components/organizer/events/EventFormModal.jsx
import React, { useState, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  createEvent, 
  updateEvent, 
  fetchEventDetails 
} from "@/store/slices/organizerSlice";
import { 
  Calendar, MapPin, Users, DollarSign, Route, 
  Send, ArrowLeft, Plus, Trash2, Package,
  ChevronDown, ChevronUp 
} from "lucide-react";
import GPXUpload from "@/components/common/GPXUpload";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";
import TextArea from "@/components/common/TextArea";
import SelectDropdown from "@/components/common/SelectDropdown";
import DatePicker from "@/components/common/DatePicker";
import CityAutocomplete from "@/components/common/CityAutocomplete";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { INDIAN_STATES as RAW_INDIAN_STATES, EVENT_TYPES, SPONSORSHIP_COMPONENTS } from "@/utils/constants";

const INDIAN_STATES = RAW_INDIAN_STATES
  .filter(state => state.code) // Filter out the "Select a State" placeholder
  .map(state => ({
    value: state.code,
    label: state.name
  }));

const EventFormModal = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentEvent, isLoading } = useSelector((state) => state.organizer);
  const [isPaid, setIsPaid] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      event_type: "",
      start_date: "",
      end_date: "",
      capacity: "",
      is_paid: false,
      entry_fee: 0,
      location: {
        address: "",
        city: "",
        state_code: "",
      },
      event_route: {
        name: "",
        distance: "",
        start_point: "",
        end_point: "",
      },
      sponsorship_components: [],
      landmarks: [],
      gpxFile: null,
    },
  });

  const { fields: sponsorComponents, append: appendSponsor, remove: removeSponsor } = useFieldArray({
    control,
    name: "sponsorship_components",
  });

  const { fields: landmarkFields, append: appendLandmark, remove: removeLandmark } = useFieldArray({
    control,
    name: "landmarks",
  });

  // State to track expanded landmarks
  const [expandedLandmarks, setExpandedLandmarks] = useState({});

  const toggleLandmark = (index) => {
    setExpandedLandmarks(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchEventDetails(id));
    }
  }, [dispatch, isEdit, id]);

  useEffect(() => {
    if (isEdit && currentEvent) {
      const formData = {
        ...currentEvent,
        start_date: currentEvent.start_date?.split("T")[0] || "",
        end_date: currentEvent.end_date?.split("T")[0] || "",
        location: currentEvent.location || { address: "", city: "", state_code: "" },
        event_route: currentEvent.event_route || { name: "", distance: "", start_point: "", end_point: "" },
        sponsorship_components: (currentEvent.sponsorship_components || []).map(c => ({
          ...c,
          type: c.type || "monetary"
        })),
        landmarks: currentEvent.landmarks || [],
      };
      reset(formData);
      setIsPaid(currentEvent.is_paid || false);
    }
  }, [isEdit, currentEvent, reset]);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      // Append simple fields
      formData.append("title", data.title);
      formData.append("description", data.description);
      formData.append("event_type", data.event_type);
      formData.append("start_date", data.start_date);
      formData.append("end_date", data.end_date); // Assuming backend expects string dates
      
      // Handle paid event fields
      formData.append("is_paid", isPaid);
      if (isPaid) {
        formData.append("entry_fee", Number(data.entry_fee));
      } else {
        formData.append("entry_fee", 0);
      }

      // Capacity is optional now
      if (data.capacity) {
        formData.append("capacity", Number(data.capacity));
      }

      // Handle Nested Objects (Stringified JSON)
      formData.append("location", JSON.stringify(data.location));
      
      // Route details
      const routeData = {
        ...data.event_route,
        distance: data.event_route.distance ? Number(data.event_route.distance) : undefined,
        landmarks: (data.landmarks || []).map(l => ({
          ...l,
          km_mark: (l.km_mark !== "" && l.km_mark !== null && l.km_mark !== undefined) ? Number(l.km_mark) : undefined,
          description: l.description?.trim() || undefined,
          coordinates: (l.coordinates && l.coordinates.length > 0) ? l.coordinates : undefined,
        })),
      };
      formData.append("event_route", JSON.stringify(routeData));

      // Arrays
      const sponsorshipComponents = (data.sponsorship_components || []).map(comp => ({
        ...comp,
        value: comp.value ? Number(comp.value) : 0
      }));
      formData.append("sponsorship_components", JSON.stringify(sponsorshipComponents));

      // Append GPX File
      if (data.gpxFile) {
        formData.append("gpxFile", data.gpxFile);
      }

      if (isEdit) {
        await dispatch(updateEvent({ eventId: id, eventData: formData })).unwrap();
        toast.success("Event updated successfully");
      } else {
        // POST /api/events/create handles both creation and submission for review
        await dispatch(createEvent(formData)).unwrap();
        toast.success("Event submitted for review");
      }

      navigate("/organizer/events");
    } catch (error) {
      toast.error(error.message || "Failed to save event");
    }
  };

  const handleSubmitForReview = () => {
    handleSubmit((data) => onSubmit(data))();
  };

  const addSponsorComponent = () => {
    appendSponsor({
      id: uuidv4(),
      category: "",
      type: "monetary",
      description: "",
      value: "",
    });
  };

  const addLandmark = () => {
    appendLandmark({
      name: "",
      km_mark: "",
      description: "",
      coordinates: [], // [lng, lat] - potentially could be added via map later
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/organizer/events")}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {isEdit ? "Edit Event" : "Create New Event"}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEdit ? "Update your event details" : "Fill in the details for your new event"}
          </p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
      >
        <form className="space-y-8">
          {/* Basic Details */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              Basic Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <TextInput
                  label="Event Title *"
                  placeholder="Enter event title"
                  error={errors.title?.message}
                  {...register("title", { required: "Title is required" })}
                />
              </div>
              <div className="md:col-span-2">
                <TextArea
                  label="Description *"
                  placeholder="Describe your event..."
                  rows={4}
                  error={errors.description?.message}
                  {...register("description", { required: "Description is required" })}
                />
              </div>
              <Controller
                name="event_type"
                control={control}
                rules={{ required: "Event type is required" }}
                render={({ field }) => (
                  <SelectDropdown
                    label="Event Type *"
                    options={EVENT_TYPES}
                    placeholder="Select event type"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={errors.event_type?.message}
                  />
                )}
              />
              <TextInput
                label="Capacity"
                type="number"
                icon={<Users className="w-5 h-5" />}
                placeholder="Maximum participants (0 for unlimited)"
                error={errors.capacity?.message}
                {...register("capacity", { min: 0 })}
              />
              <Controller
                name="start_date"
                control={control}
                rules={{ 
                  required: "Start date is required",
                  validate: value => {
                    const selected = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return selected >= today || "Start date must be in the future";
                  }
                }}
                render={({ field }) => (
                  <DatePicker
                    label="Start Date *"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.start_date?.message}
                    allowFuture={true}
                  />
                )}
              />
              <Controller
                name="end_date"
                control={control}
                rules={{ 
                  required: "End date is required",
                  validate: value => {
                    const start = new Date(watch("start_date"));
                    const end = new Date(value);
                    return !start || end >= start || "End date must be after start date";
                  }
                }}
                render={({ field }) => (
                  <DatePicker
                    label="End Date *"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.end_date?.message}
                    allowFuture={true}
                  />
                )}
              />
            </div>

            {/* Paid Event Toggle */}
            <div className="mt-4 flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPaid}
                  onChange={(e) => setIsPaid(e.target.checked)}
                  className="w-5 h-5 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                />
                <span className="text-gray-700 dark:text-gray-300">This is a paid event</span>
              </label>
              {isPaid && (
                <div className="w-40">
                  <TextInput
                    type="number"
                    icon={<DollarSign className="w-5 h-5" />}
                    placeholder="Entry fee (₹)"
                    {...register("entry_fee")}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Location */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-600" />
              Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextInput
                label="Address"
                placeholder="Event address"
                {...register("location.address")}
              />
              <Controller
                name="location.city"
                control={control}
                render={({ field }) => (
                  <CityAutocomplete
                    label="City"
                    placeholder="City name"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.location?.city?.message}
                  />
                )}
              />
              <Controller
                name="location.state_code"
                control={control}
                render={({ field }) => (
                  <SelectDropdown
                    label="State"
                    options={INDIAN_STATES}
                    placeholder="Select state"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </section>

          {/* Route Details */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Route className="w-5 h-5 text-amber-600" />
              Route Details
            </h3>
            
            {/* GPX File Upload */}
            <div className="mb-6">
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  GPX Route File 
               </label>
               <Controller
                 name="gpxFile"
                 control={control}
                 rules={{ required: !isEdit && "GPX file is required" }}
                 render={({ field }) => (
                   <GPXUpload 
                     onUpload={(file) => field.onChange(file)}
                     className="bg-white"
                   />
                 )}
               />
               <p className="text-xs text-gray-500 mt-2">
                 Upload a .gpx file to automatically extract route path and data.
               </p>
               {errors.gpxFile && (
                 <p className="text-sm text-red-500 mt-1">{errors.gpxFile.message}</p>
               )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Route Name *"
                placeholder="e.g., Mountain Trail"
                error={errors.event_route?.name?.message}
                {...register("event_route.name", { required: "Route name is required" })}
              />
              <TextInput
                label="Distance (km) *"
                type="number"
                step="0.01"
                placeholder="Route distance"
                error={errors.event_route?.distance?.message}
                {...register("event_route.distance", { required: "Distance is required" })}
              />
              <TextInput
                label="Start Point *"
                placeholder="Starting location"
                error={errors.event_route?.start_point?.message}
                {...register("event_route.start_point", { required: "Start point is required" })}
              />
              <TextInput
                label="End Point *"
                placeholder="Ending location"
                error={errors.event_route?.end_point?.message}
                {...register("event_route.end_point", { required: "End point is required" })}
              />
            </div>
          </section>

          {/* Landmarks */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-600" />
                Landmarks
              </h3>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addLandmark}
                className="dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Landmark
              </Button>
            </div>

            {landmarkFields.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                No landmarks added. Add key points along the route.
              </p>
            ) : (
              <div className="space-y-3">
                {landmarkFields.map((field, index) => {
                  const isExpanded = expandedLandmarks[index];
                  return (
                    <div
                      key={field.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
                    >
                      <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900/30">
                        <div className="flex items-center gap-3 flex-1">
                          <button
                             type="button"
                             onClick={() => toggleLandmark(index)}
                             className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                             {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          
                          {/* Name Field always visible */}
                          <div className="flex-1 max-w-sm">
                            <TextInput
                              placeholder="Landmark Name *"
                              className="mb-0 bg-white dark:bg-gray-800"
                              error={errors.landmarks?.[index]?.name?.message}
                              {...register(`landmarks.${index}.name`, { 
                                required: "Name is required",
                                maxLength: { value: 100, message: "Max 100 characters" }
                              })}
                            />
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeLandmark(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 dark:border-gray-700">
                           <TextInput
                            label="Km Mark"
                            type="number"
                            step="0.1"
                            placeholder="e.g. 5.2"
                            {...register(`landmarks.${index}.km_mark`, { min: 0 })}
                          />
                          <div className="md:col-span-2">
                            <TextInput
                              label="Description"
                              placeholder="Brief description of the landmark..."
                              {...register(`landmarks.${index}.description`, {
                                maxLength: { value: 300, message: "Max 300 characters" }
                              })}
                            />
                          </div>
                          {/* Coordinates can be added here later if needed, hidden for now as it's typically auto-populated or map-picked */}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Sponsorship Components */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-600" />
                Sponsorship Components
              </h3>
              <Button type="button" variant="outline" size="sm" onClick={addSponsorComponent} className="dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700">
                <Plus className="w-4 h-4 mr-1" /> Add Component
              </Button>
            </div>

            {sponsorComponents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                No sponsorship components added. Add components that sponsors can support.
              </p>
            ) : (
              <div className="space-y-3">
                {sponsorComponents.map((field, index) => {
                  const currentType = watch(`sponsorship_components.${index}.type`);
                  return (
                    <div
                      key={field.id}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/20"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Component {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeSponsor(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Controller
                          name={`sponsorship_components.${index}.category`}
                          control={control}
                          rules={{ required: "Category is required" }}
                          render={({ field }) => (
                            <SelectDropdown
                              label="Category *"
                              options={SPONSORSHIP_COMPONENTS}
                              placeholder="Select component"
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              error={errors.sponsorship_components?.[index]?.category?.message}
                            />
                          )}
                        />
                        <Controller
                          name={`sponsorship_components.${index}.type`}
                          control={control}
                          render={({ field }) => (
                            <SelectDropdown
                              label="Type"
                              options={[
                                { value: "monetary", label: "Monetary" },
                                { value: "kind", label: "In-Kind" }
                              ]}
                              value={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                if (e.target.value === "kind") {
                                  setValue(`sponsorship_components.${index}.value`, 0); // Reset value if kind
                                }
                              }}
                            />
                          )}
                        />
                        <div className={currentType === "kind" ? "lg:col-span-2" : ""}>
                          <TextInput
                            label="Description"
                            placeholder="Brief description"
                            {...register(`sponsorship_components.${index}.description`)}
                          />
                        </div>
                        {currentType !== "kind" && (
                          <TextInput
                            label="Value (₹)"
                            type="number"
                            placeholder="Amount"
                            {...register(`sponsorship_components.${index}.value`, { 
                              required: currentType === "monetary" ? "Value is required" : false 
                            })}
                            error={errors.sponsorship_components?.[index]?.value?.message}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-100 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/organizer/events")}
              className="flex-1 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmitForReview}
              loading={isLoading}
              className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
            >
              <Send className="w-4 h-4 mr-2" />
              {isEdit ? "Update & Submit for Review" : "Submit for Review"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EventFormModal;
